import { randomUUID } from 'node:crypto';
import type { Agent } from '../agent';
import { RunCompactBoundaryItem, RunCompactSummaryItem } from '../items';
import type { Model } from '../model';
import type { AgentInputItem } from '../types';
import { Usage } from '../usage';
import { assembleModelContext } from './assembler';
import { type CompactionPlan, planCompactionBoundary } from './planner';
import { compactSummaryToModelMessage } from './prompt';
import { buildStableHistoryItems } from './refs';
import { isValidSessionMemoryContent } from './session-memory-updater';
import { summarizeForCompaction } from './summarizer';
import {
  calculateCompactionThreshold,
  estimateTokensForItems,
} from './token-estimator';
import type {
  CompactBoundaryRecord,
  CompactionRecord,
  CompactSource,
  CompactSummaryRecord,
  CompactTrigger,
  ResolvedLocalCompactionConfig,
  SessionMemoryStore,
} from './types';

export type LocalCompactionResult = {
  didCompact: boolean;
  id?: string;
  source?: CompactSource;
  boundaryItem?: CompactBoundaryRecord;
  summaryItem?: CompactSummaryRecord;
  modelInput?: AgentInputItem[];
  runItems: (RunCompactBoundaryItem | RunCompactSummaryItem)[];
  usage: Usage;
  preCompactTokenCount?: number;
  postCompactTokenCount?: number;
  summarizedItemCount?: number;
  preservedItemCount?: number;
};

export type RunLocalCompactionOptions = {
  agent: Agent<any, any>;
  model: Model;
  input: AgentInputItem[];
  existingRecords?: CompactionRecord[];
  config: ResolvedLocalCompactionConfig;
  trigger: CompactTrigger;
  reason?: 'threshold' | 'manual' | 'context_length_exceeded';
  instructions?: string;
  sessionId?: string;
  sessionMemoryStore?: SessionMemoryStore;
  signal?: AbortSignal;
  force?: boolean;
  onStarted?: (data: {
    id: string;
    source: CompactSource;
    preCompactTokenCount: number;
    summarizedItemCount: number;
    preservedItemCount: number;
  }) => void;
};

export async function runLocalCompaction(
  options: RunLocalCompactionOptions,
): Promise<LocalCompactionResult> {
  const plan = await buildCompactionPlan(options);
  if (!plan) {
    return { didCompact: false, runItems: [], usage: new Usage() };
  }

  const compactId = randomUUID();
  const createdAt = new Date().toISOString();
  const previousBoundary = [...(options.existingRecords ?? [])]
    .reverse()
    .find(
      (record): record is CompactBoundaryRecord =>
        record.type === 'compact_boundary',
    );

  let summaryContent = '';
  let source: CompactSource = 'model_summary';
  let usage = new Usage();

  if (plan.sessionMemorySummary) {
    summaryContent = plan.sessionMemorySummary;
    source = 'session_memory';
  }
  options.onStarted?.({
    id: compactId,
    source,
    preCompactTokenCount: plan.estimatedPreTokens,
    summarizedItemCount: plan.itemsForSummary.length,
    preservedItemCount: plan.preservedItems.length,
  });

  if (!plan.sessionMemorySummary) {
    const summary = await summarizeForCompaction({
      model: options.model,
      items: plan.itemsForSummary,
      instructions: options.instructions,
      modelSettings: {
        maxTokens: options.config.maxSummaryTokens,
        ...(options.config.modelSettings ?? {}),
      },
      signal: options.signal,
    });
    summaryContent = summary.summary;
    usage = summary.usage;
  }

  const summaryItem: CompactSummaryRecord = {
    type: 'compact_summary',
    id: `compact_summary_${compactId}`,
    boundaryId: `compact_boundary_${compactId}`,
    content: summaryContent,
    source,
    createdAt,
  };

  const durableBoundary = projectDurableBoundary({
    input: options.input,
    plan,
    existingRecords: options.existingRecords ?? [],
    previousBoundary,
  });
  const compactedModelInput = assembleModelContext(options.input, [
    {
      type: 'compact_boundary',
      id: `compact_boundary_${compactId}`,
      summaryItemId: summaryItem.id,
      compactedRange: plan.compactedRange,
      insertAfterItemId: plan.insertAfterItemId,
      ...(previousBoundary
        ? { supersedesBoundaryId: previousBoundary.id }
        : {}),
      trigger: options.trigger,
      source,
      createdAt,
      status: 'completed',
    },
    summaryItem,
  ]);
  const postCompactTokenCount = estimateTokensForItems(compactedModelInput);
  const boundaryItem: CompactBoundaryRecord = {
    type: 'compact_boundary',
    id: `compact_boundary_${compactId}`,
    summaryItemId: summaryItem.id,
    compactedRange: durableBoundary.compactedRange,
    insertAfterItemId: durableBoundary.insertAfterItemId,
    ...(durableBoundary.supersedesBoundaryId
      ? { supersedesBoundaryId: durableBoundary.supersedesBoundaryId }
      : {}),
    trigger: options.trigger,
    source,
    createdAt,
    status: 'completed',
    preCompactTokenCount: plan.estimatedPreTokens,
    postCompactTokenCount,
    summarizedItemCount: plan.itemsForSummary.length,
    preservedItemCount: plan.preservedItems.length,
    summaryTokenCount: estimateTokensForItems([
      {
        type: 'message',
        role: 'user',
        content: summaryContent,
      },
    ]),
  };

  const runItems = [
    new RunCompactBoundaryItem(boundaryItem, options.agent),
    new RunCompactSummaryItem(summaryItem, options.agent),
  ];
  return {
    didCompact: true,
    id: compactId,
    source,
    boundaryItem,
    summaryItem,
    modelInput: compactedModelInput,
    runItems,
    usage,
    preCompactTokenCount: plan.estimatedPreTokens,
    postCompactTokenCount,
    summarizedItemCount: plan.itemsForSummary.length,
    preservedItemCount: plan.preservedItems.length,
  };
}

type InternalCompactionPlan = CompactionPlan & {
  sessionMemorySummary?: string;
};

async function buildCompactionPlan(
  options: RunLocalCompactionOptions,
): Promise<InternalCompactionPlan | null> {
  const thresholdTokens = calculateCompactionThreshold(options.config);
  const force = options.force ?? options.trigger === 'manual';
  const estimatedPreTokens = estimateTokensForItems(options.input);

  if (!force && estimatedPreTokens < thresholdTokens) {
    return null;
  }

  const sessionMemoryPlan = await tryBuildSessionMemoryPlan(options);
  if (sessionMemoryPlan?.sessionMemorySummary) {
    const estimatedPostTokens =
      estimateTokensForItems(sessionMemoryPlan.preservedItems) +
      estimateTokensForItems([
        {
          type: 'message',
          role: 'user',
          content: sessionMemoryPlan.sessionMemorySummary,
        },
      ]);
    if (force || estimatedPostTokens < thresholdTokens) {
      return sessionMemoryPlan;
    }
  }

  return planCompactionBoundary(options.input, {
    thresholdTokens,
    force,
    preservedTailTokens: options.config.preservedTailTokens,
    preservedTailMessages: options.config.preservedTailMessages,
    minTextMessagesToKeep: options.config.minTextMessagesToKeep,
    estimatedSummaryTokens: options.config.maxSummaryTokens,
  });
}

async function tryBuildSessionMemoryPlan(
  options: RunLocalCompactionOptions,
): Promise<InternalCompactionPlan | null> {
  if (
    !options.sessionId ||
    !options.sessionMemoryStore ||
    options.instructions?.trim()
  ) {
    return null;
  }
  const snapshot = await options.sessionMemoryStore.read(options.sessionId);
  if (
    !snapshot?.content ||
    !snapshot.lastSummarizedItemId ||
    !isValidSessionMemoryContent(snapshot.content)
  ) {
    return null;
  }
  const stableItems = buildStableHistoryItems(options.input);
  const anchor = stableItems.find(
    (entry) => entry.id === snapshot.lastSummarizedItemId,
  );
  const first = stableItems.find(({ item }) => !isSystemMessage(item));
  if (!anchor || !first || anchor.index < first.index) {
    return null;
  }
  const itemsForSummary = options.input
    .slice(first.index, anchor.index + 1)
    .filter((item) => !isSystemMessage(item));
  const preservedItems = options.input.slice(anchor.index + 1);
  if (itemsForSummary.length === 0 || preservedItems.length === 0) {
    return null;
  }
  const estimatedPostTokens =
    estimateTokensForItems(preservedItems) +
    estimateTokensForItems([
      {
        type: 'message',
        role: 'user',
        content: snapshot.content,
      },
    ]);
  return {
    compactedRange: {
      fromItemId: first.id,
      toItemId: anchor.id,
    },
    insertAfterItemId: anchor.id,
    itemsForSummary,
    preservedItems,
    estimatedPreTokens: estimateTokensForItems(options.input),
    estimatedPostTokens,
    sessionMemorySummary: snapshot.content,
  };
}

function isSystemMessage(item: AgentInputItem): boolean {
  return (
    item &&
    typeof item === 'object' &&
    item.type === 'message' &&
    item.role === 'system'
  );
}

type DurableRange = {
  fromItemId: string;
  toItemId: string;
  boundaryIds: Set<string>;
};

function projectDurableBoundary(options: {
  input: AgentInputItem[];
  plan: CompactionPlan;
  existingRecords: CompactionRecord[];
  previousBoundary?: CompactBoundaryRecord;
}): {
  compactedRange: { fromItemId: string; toItemId: string };
  insertAfterItemId: string;
  supersedesBoundaryId?: string;
} {
  const refs = buildStableHistoryItems(options.input);
  const ranges = buildDurableRanges(refs, options.existingRecords);
  const fromRange = ranges.get(options.plan.compactedRange.fromItemId);
  const toRange = ranges.get(options.plan.compactedRange.toItemId);
  const fromIndex = refs.findIndex(
    (entry) => entry.id === options.plan.compactedRange.fromItemId,
  );
  const toIndex = refs.findIndex(
    (entry) => entry.id === options.plan.compactedRange.toItemId,
  );
  const compactedBoundaryIds = new Set<string>();
  if (fromIndex >= 0 && toIndex >= fromIndex) {
    for (let index = fromIndex; index <= toIndex; index += 1) {
      const range = ranges.get(refs[index]!.id);
      for (const boundaryId of range?.boundaryIds ?? []) {
        compactedBoundaryIds.add(boundaryId);
      }
    }
  }
  const compactedRange = {
    fromItemId: fromRange?.fromItemId ?? options.plan.compactedRange.fromItemId,
    toItemId: toRange?.toItemId ?? options.plan.compactedRange.toItemId,
  };
  const supersedesPreviousBoundary =
    options.previousBoundary &&
    (compactedBoundaryIds.has(options.previousBoundary.id) ||
      inputRangeCoversBoundary(refs, compactedRange, options.previousBoundary));
  const supersedesBoundaryId =
    options.previousBoundary && supersedesPreviousBoundary
      ? options.previousBoundary.id
      : undefined;

  return {
    compactedRange,
    insertAfterItemId: toRange?.toItemId ?? options.plan.insertAfterItemId,
    ...(supersedesBoundaryId ? { supersedesBoundaryId } : {}),
  };
}

function buildDurableRanges(
  refs: ReturnType<typeof buildStableHistoryItems>,
  records: CompactionRecord[],
): Map<string, DurableRange> {
  const ranges = new Map<string, DurableRange>();
  for (const ref of refs) {
    ranges.set(ref.id, {
      fromItemId: ref.id,
      toItemId: ref.id,
      boundaryIds: new Set(),
    });
  }

  const boundaries = new Map<string, CompactBoundaryRecord>();
  const summaries: CompactSummaryRecord[] = [];
  for (const record of records) {
    if (record.type === 'compact_boundary' && record.status === 'completed') {
      boundaries.set(record.id, record);
    } else if (record.type === 'compact_summary') {
      summaries.push(record);
    }
  }

  for (const summary of summaries) {
    const boundary = boundaries.get(summary.boundaryId);
    if (!boundary) {
      continue;
    }
    for (const ref of refs) {
      if (!isCompactSummaryMessage(ref.item, summary.content)) {
        continue;
      }
      ranges.set(ref.id, {
        fromItemId: boundary.compactedRange.fromItemId,
        toItemId: boundary.compactedRange.toItemId,
        boundaryIds: new Set([boundary.id]),
      });
    }
  }

  return ranges;
}

function isCompactSummaryMessage(
  item: AgentInputItem,
  summaryContent: string,
): boolean {
  if (!item || typeof item !== 'object' || item.type !== 'message') {
    return false;
  }
  const expected = compactSummaryToModelMessage(summaryContent) as {
    role?: unknown;
    content?: unknown;
  };
  const message = item as { role?: unknown; content?: unknown };
  return message.role === expected.role && message.content === expected.content;
}

function inputRangeCoversBoundary(
  refs: ReturnType<typeof buildStableHistoryItems>,
  range: { fromItemId: string; toItemId: string },
  boundary: CompactBoundaryRecord,
): boolean {
  const indexById = new Map(refs.map((entry) => [entry.id, entry.index]));
  const rangeFrom = indexById.get(range.fromItemId);
  const rangeTo = indexById.get(range.toItemId);
  const boundaryFrom = indexById.get(boundary.compactedRange.fromItemId);
  const boundaryTo = indexById.get(boundary.compactedRange.toItemId);
  return (
    typeof rangeFrom === 'number' &&
    typeof rangeTo === 'number' &&
    typeof boundaryFrom === 'number' &&
    typeof boundaryTo === 'number' &&
    rangeFrom <= boundaryFrom &&
    boundaryTo <= rangeTo
  );
}
